import i18n from "i18next";
import { Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClone, faInfoCircle, faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { AavoContextMenu } from "src/components/common/contextMenu/AavoContextMenu.tsx";
import { useContextMenu } from "src/components/common/contextMenu/useContextMenu.ts";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { mergeSx } from "src/utils/styles.ts";
import { getTitlePrefix } from "src/components/views/erp/configurator/configuratorForm/configuratorTree/configuratorTreeTitlePrefix.ts";
import { AddPropertyModelSubConfigurationView } from "src/components/views/erp/configurator/configuratorForm/propertyModel/AddPropertyModelSubConfigurationView.tsx";
import {
	PartConfigurationEvaluation,
	PartConfigurationSchema,
	PartConfigurationSchemaProperty,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/partConfigurationSdk.ts";
import {
	getAddableSubConfigurationProperties,
	getChildNodes,
	getNodeInputValues,
	getNodeIssues,
	getNodeLabel,
	getNodeSchemaProperties,
	getRootNode,
	PropertyInputValues,
	PropertyModelNode,
	PropertyModelNodePath,
	propertyModelNodePathKey,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/propertyModelInputTree.ts";

export interface PropertyModelConfiguratorTreeNavProps {
	schema: PartConfigurationSchema;
	evaluation: PartConfigurationEvaluation;
	rootInputValues: PropertyInputValues;
	selectedPath: PropertyModelNodePath;
	readonly: boolean;
	selectNode: (path: PropertyModelNodePath) => void;
	addChild: (parentPath: PropertyModelNodePath, property: PartConfigurationSchemaProperty) => Promise<unknown>;
	removeNode: (path: PropertyModelNodePath) => Promise<unknown>;
	cloneNode: (path: PropertyModelNodePath) => Promise<unknown>;
}

/**
 * The configurations of a property-model tree, managed as in the component form: each child is shown by its
 * property, position and part, children are added with the "+" button or the context menu, and removed or cloned from
 * the context menu.
 */
export function PropertyModelConfiguratorTreeNav(props: PropertyModelConfiguratorTreeNavProps) {
	const root = getRootNode(props.evaluation);
	return (
		<VerticalBox component={"ul"} sx={{ overflow: "auto", paddingX: 0, flex: 1 }}>
			<PropertyModelConfiguratorTreeNavItem
				{...props}
				node={root}
				label={getNodeLabel(props.schema, root)}
				isListElement={false}
			/>
		</VerticalBox>
	);
}

interface PropertyModelConfiguratorTreeNavItemProps extends PropertyModelConfiguratorTreeNavProps {
	node: PropertyModelNode;
	label: string;
	isListElement: boolean;
}

function PropertyModelConfiguratorTreeNavItem(props: PropertyModelConfiguratorTreeNavItemProps) {
	const { schema, evaluation, rootInputValues, node, label, selectedPath, readonly, selectNode } = props;
	const { palette } = useTheme();
	const [openContextMenu, contextMenuState] = useContextMenu();
	const { openDialog } = useGenericDialog();

	const isSelected = propertyModelNodePathKey(node.path) === propertyModelNodePathKey(selectedPath);
	const issues = getNodeIssues(evaluation, node.path);
	const addableProperties =
		readonly ?
			[]
		:	getAddableSubConfigurationProperties(
				getNodeSchemaProperties(schema, node),
				node,
				getNodeInputValues(rootInputValues, node.path) ?? {},
			);

	return (
		<>
			<HorizontalBox
				component={"li"}
				onClick={() => selectNode(node.path)}
				onContextMenu={(event) => {
					event.preventDefault();
					selectNode(node.path);
					if (readonly || (addableProperties.length === 0 && node.path.length === 0)) return;
					openContextMenu({
						mouseEvent: event,
						content: (
							<TreeItemContextMenu
								{...props}
								addableProperties={addableProperties}
								openAddDialog={openAddDialog}
							/>
						),
					});
				}}
				sx={mergeSx(
					{
						flex: "fit-content 0 0",
						alignItems: "center",
						gap: 1,
						paddingY: 1.25,
						paddingLeft: 1 + node.path.length * 2,
						cursor: "pointer",
						":hover": { backgroundColor: palette.grey[100] },
					},
					isSelected && { backgroundColor: alpha(palette.primary.light, 0.1) },
				)}
			>
				{/* Menu clicks bubble through the portal; they must not select this item again, e.g. after a clone. */}
				<span onClick={(event) => event.stopPropagation()}>
					<AavoContextMenu state={contextMenuState} />
				</span>
				<Typography sx={{ fontWeight: "bold", marginRight: 1 }}>
					{`${getTitlePrefix(node.path.length)} ${label}`}
				</Typography>
				{issues.length > 0 && (
					<Tooltip
						title={issues.map((issue, index) => (
							<Typography key={index}>{issue.message}</Typography>
						))}
					>
						<FontAwesomeIcon icon={faInfoCircle} color={palette.error.main} />
					</Tooltip>
				)}
				{addableProperties.length > 0 && (
					<AsyncButton
						icon={faPlus}
						tooltip={i18n.t("add_sub_configuration")}
						size={"small"}
						sx={{ paddingX: 0.5 }}
						onClick={async (event) => {
							event.stopPropagation();
							openAddDialog();
						}}
					/>
				)}
			</HorizontalBox>
			{getChildNodes(node).flatMap(({ isList, nodes }) =>
				nodes.map((child) => (
					<PropertyModelConfiguratorTreeNavItem
						{...props}
						key={propertyModelNodePathKey(child.path)}
						node={child}
						label={getNodeLabel(schema, child)}
						isListElement={isList}
					/>
				)),
			)}
		</>
	);

	function openAddDialog() {
		openDialog(({ closeDialog }) => ({
			title: i18n.t("add_sub_configuration"),
			size: "lg",
			content: (
				<AddPropertyModelSubConfigurationView
					properties={addableProperties}
					addChild={async (property) => {
						await closeDialog();
						await props.addChild(node.path, property);
					}}
				/>
			),
			agentContext: {
				key: defineAgentUiContextKey("erp.configurator.addPropertyModelSubConfiguration.dialog"),
				title: i18n.t("add_sub_configuration"),
				pathSegments: [label],
				data: { propertyNames: addableProperties.map((property) => property.name) },
			},
		}));
	}
}

interface TreeItemContextMenuProps extends PropertyModelConfiguratorTreeNavItemProps {
	addableProperties: PartConfigurationSchemaProperty[];
	openAddDialog: () => void;
}

function TreeItemContextMenu({
	node,
	isListElement,
	readonly,
	removeNode,
	cloneNode,
	addableProperties,
	openAddDialog,
}: TreeItemContextMenuProps) {
	const showConfirmDialog = useConfirmDialog();
	const isRoot = node.path.length === 0;

	return [
		!readonly && addableProperties.length > 0 && (
			<AsyncMenuButton
				key={"addSubConfiguration"}
				icon={faPlus}
				label={i18n.t("add_sub_configuration")}
				onClick={async () => openAddDialog()}
			/>
		),
		!readonly && !isRoot && (
			<AsyncMenuButton
				key={"remove"}
				icon={faTrash}
				label={i18n.t("remove")}
				onClick={async () => {
					const confirmed = await showConfirmDialog();
					if (!confirmed) return;
					await removeNode(node.path);
				}}
			/>
		),
		!readonly && isListElement && (
			<AsyncMenuButton
				key={"clone"}
				icon={faClone}
				label={i18n.t("clone")}
				onClick={() => cloneNode(node.path)}
			/>
		),
	];
}
